import { Alert, Button, ButtonRow, Heading } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { getSelectedLanguage } from '@frontend/translate';
import type { ValidationErrors } from '../../../utils/validation';
import { SERIAL_NUMBER_LENGTH, serviceDeskEmailAddress } from '../../../../common/constants';
import { TwoFactorAuthenticationType } from '../../../../common/enums/twoFactorAuthenticationType';
import { ValidationErrorCode } from '../../../../common/enums/validationErrorCode';
import { loginCredentialsSchema } from '../../../../common/validation/practitioner';
import { fetchJson } from '../../../store/fetch';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../utils/validation';
import { LoginMfaHelpFooter } from '../../elements/LoginMfaHelpFooter';
import { HardwareTokenInfo } from './HardwareTokenInfo';
import { HardwareTokenSerialNumber } from './HardwareTokenSerialNumber';
import { MfaCodeInput } from './MfaCodeInput';
import { MfaLoginCredentialsInput } from './MfaLoginCredentialsInput';
import { MfaSuccessScreen } from './MfaSuccessScreen';

interface ISetHardwareTokenProps {
    goBack?: () => void;
    goToNextStep?: () => void;
    isActivationFlow?: boolean;
    onSuccess?: (success: boolean) => void;
    password?: string;
    redirectHome?: boolean;
    token?: string;
    userName?: string;
}

export function SetHardwareToken(props: ISetHardwareTokenProps) {
    const translate = getSelectedLanguage();
    const [userName, setUserName] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [isFormDisabled, setIsFormDisabled] = useState<boolean>(false);
    const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null);
    const [errorCode, setErrorCode] = useState<ValidationErrorCode | null>(null);
    const [wizardStep, setWizardStep] = useState<number>(0);
    const [invalidToken, setInvalidToken] = useState<boolean>(false);
    const [serialNumber, setSerialNumber] = useState<string>('');
    const [wrongCodeCounter, setWrongCodeCounter] = useState<number>(0);

    useEffect(() => {
        loadToken();
    }, []);

    useEffect(() => {
        if (props.onSuccess) {
            props.onSuccess(wizardStep === 4);
        }
    }, [wizardStep]);

    useEffect(() => {
        if (wizardStep === 1) {
            setIsFormDisabled(!password);
        }
    }, [password]);

    return (
        <>
            {wizardStep !== 4 && (
                <Heading>
                    {props.token && <>{props.isActivationFlow ? 'Account activeren' : 'Hardware token registeren'}</>}
                    {!props.token && <>Tweestapsverificatie instellen</>}
                </Heading>
            )}
            {invalidToken && (
                <Alert
                    appearance="warning"
                    noMargin={true}
                >
                    Deze link is niet langer geldig. Bel{' '}
                    <Alert.Link
                        href="tel:+31 20 600 00 60"
                        variant="externalLink"
                    >
                        020-600 00 60
                    </Alert.Link>{' '}
                    of mail naar{' '}
                    <Alert.Link
                        href={`mailto:${serviceDeskEmailAddress}`}
                        variant="externalLink"
                    >
                        {serviceDeskEmailAddress}
                    </Alert.Link>
                    .
                </Alert>
            )}
            {wizardStep === 0 && <HardwareTokenInfo />}
            {wizardStep === 1 && (
                <MfaLoginCredentialsInput
                    errorCode={errorCode}
                    errors={errors}
                    isFormDisabled={false}
                    onChange={onChange}
                    onDoValidate={onDoValidate}
                    password={props.password ? props.password : password}
                    remainingAttempts={remainingAttempts}
                    submitCredentials={submitCredentials}
                    userName={props.userName ? props.userName : userName}
                />
            )}
            {wizardStep === 2 && (
                <HardwareTokenSerialNumber
                    {...props}
                    onValidSerialNumber={(newSerialNumber) => setSerialNumber(newSerialNumber)}
                    password={props.password ? props.password : password}
                    placeHolder={serialNumber}
                    setIsFormDisabled={(disabled: boolean) => setIsFormDisabled(disabled)}
                    userName={props.userName ? props.userName : userName}
                />
            )}
            {wizardStep === 3 && (
                <MfaCodeInput
                    activationToken={props.token}
                    mfaType={TwoFactorAuthenticationType.verificationDevice}
                    onCodeFailed={() => setWrongCodeCounter(wrongCodeCounter + 1)}
                    onCodeVerified={onMfaVerified}
                    password={props.password ? props.password : password}
                    serial={serialNumber}
                    userName={props.userName ? props.userName : userName}
                    wrongCodeCounter={wrongCodeCounter}
                />
            )}
            {wizardStep === 4 && (
                <MfaSuccessScreen
                    mfaType={TwoFactorAuthenticationType.verificationDevice}
                    redirectToHome={props.redirectHome}
                />
            )}
            <ButtonRow>{getButtons()}</ButtonRow>
            {wizardStep !== 4 && <LoginMfaHelpFooter instructionsVideo="hardwareToken" />}
        </>
    );

    function onMfaVerified() {
        setTimeout(() => setWizardStep(wizardStep + 1), 1000);
    }

    async function nextWizardStep() {
        switch (wizardStep) {
            case 0:
                setIsFormDisabled(!password);
                setWizardStep(props.isActivationFlow ? 2 : 1);
                break;
            case 1:
                await submitCredentials();
                break;
            case 2:
                if (serialNumber) {
                    setWizardStep(wizardStep + 1);
                }
                break;
            default:
                setWizardStep(wizardStep + 1);
        }
    }

    function previousWizardStep() {
        setIsFormDisabled(false);
        props.isActivationFlow && wizardStep === 2 ? setWizardStep(0) : setWizardStep(wizardStep - 1);
    }

    function getButtons() {
        const buttons: JSX.Element[] = [];

        if (wizardStep < 3) {
            buttons.push(
                <Button
                    color="secondary"
                    isDisabled={isFormDisabled || (wizardStep === 2 && serialNumber.length !== SERIAL_NUMBER_LENGTH)}
                    key="next"
                    onClick={nextWizardStep}
                    width="full"
                >
                    {translate.common.next}
                </Button>
            );
        }
        if (wizardStep > 0 && wizardStep < 4) {
            buttons.push(
                <Button
                    color="secondary"
                    key="prev"
                    onClick={() => {
                        previousWizardStep();
                        setWrongCodeCounter(0);
                    }}
                    variant="outline"
                    width="full"
                >
                    {translate.common.previous}
                </Button>
            );
        } else if (wizardStep === 0 && props.goBack) {
            buttons.push(
                <Button
                    color="secondary"
                    key="prev"
                    onClick={() => props.goBack && props.goBack()}
                    variant="outline"
                    width="full"
                >
                    {translate.common.previous}
                </Button>
            );
        }

        return buttons;
    }

    function onDoValidate(name: 'userName' | 'password') {
        setErrors(updateValidationErrorsForOne(loginCredentialsSchema, { userName, password }, errors, name));
    }

    function onChange(name: 'userName' | 'password', value: string) {
        if (name === 'userName') {
            setUserName(value);
        } else if (name === 'password') {
            setPassword(value);
        }
    }

    async function submitCredentials() {
        const validationResult = validate(loginCredentialsSchema, { userName, password });
        setErrors(validationResult.errors);

        if (errors.size > 0) {
            return;
        }

        const result = await fetchJson(
            `/api/checkCredentials?resetMfaToken=${props.token}`,
            'POST',
            { userName, password },
            [
                ValidationErrorCode.USERNAME_NOT_FOUND,
                ValidationErrorCode.USER_NOT_REGISTERED,
                ValidationErrorCode.WRONG_PASSWORD,
                ValidationErrorCode.WRONG_PASSWORD_WARNING,
                ValidationErrorCode.USER_BLOCKED_TEMPORARILY,
                ValidationErrorCode.USER_HAS_NO_EMPLOYMENT,
                ValidationErrorCode.USER_IS_NOT_ACTIVATED,
                ValidationErrorCode.USER_DOES_NOT_MATCH_TOKEN,
                ValidationErrorCode.MFA_SETUP_MANDATORY_LOGIN_NOT_ALLOWED,
            ]
        );

        if (result.errorCode === ValidationErrorCode.USERNAME_NOT_FOUND) {
            return setErrors(emptyValidationErrors.set('userName', result.errorCode));
        } else if (result.errorCode === ValidationErrorCode.WRONG_PASSWORD) {
            return setErrors(emptyValidationErrors.set('password', result.errorCode));
        } else if (result.errorCode === ValidationErrorCode.WRONG_PASSWORD_WARNING) {
            if (result.message) {
                setRemainingAttempts(Number(result.message));
            }
            setErrorCode(result.errorCode);

            return setErrors(emptyValidationErrors.set('password', ValidationErrorCode.WRONG_PASSWORD));
        } else if (result.errorCode === ValidationErrorCode.USER_BLOCKED_TEMPORARILY) {
            setErrorCode(result.errorCode);
            setIsFormDisabled(true);
            setUserName('');
            setPassword('');

            return setErrors(emptyValidationErrors);
        } else if (result.errorCode === ValidationErrorCode.USER_DOES_NOT_MATCH_TOKEN) {
            throw new Error('User does not match token');
        } else if (result.errorCode) {
            setErrorCode(result.errorCode as ValidationErrorCode);
        }

        setWizardStep(wizardStep + 1);
    }

    async function loadToken() {
        if (props.token) {
            const result = await fetchJson(
                'api/activationTokens/' + props.token,
                'GET',
                undefined,
                [ValidationErrorCode.ACTIVATION_TOKEN_EXPIRED, ValidationErrorCode.ACTIVATION_TOKEN_ALREADY_CONSUMED],
                [404]
            );
            if (
                result.errorCode &&
                [
                    ValidationErrorCode.ACTIVATION_TOKEN_EXPIRED,
                    ValidationErrorCode.ACTIVATION_TOKEN_ALREADY_CONSUMED,
                ].includes(result.errorCode as ValidationErrorCode)
            ) {
                setInvalidToken(true);
                setIsFormDisabled(true);
            } else {
                setUserName(result.json.practitioner.userName);
            }
        }
    }
}
